/* eslint-disable @typescript-eslint/no-magic-numbers */
// TODO: extract magic numbers into constants maybe?
import { coth, eliptical, sech, tanh } from "./mathHelpers";
const INTRINSIC_FREESPACE_IMPEDANCE = 377;
// See https://www.eeweb.com/tools/microstrip-impedance/ for the equations used here
export function microstrip(impedanceConfig) {
    const { width, thickness, height, er } = impedanceConfig;
    // Calculate the effective dielectric constant
    const erEff = calculateErEff(width, height, er);
    const wEff = width +
        (thickness / Math.PI) *
            Math.log((4 * Math.exp(1)) /
                Math.sqrt(Math.pow(thickness / height, 2) +
                    Math.pow(thickness / (width * Math.PI + 1.1 * thickness * Math.PI), 2))) *
            ((er + 1) / (2 * er));
    const x2 = Math.sqrt(16 * Math.pow(height / wEff, 2) * Math.pow((14 * er + 8) / (11 * er), 2) +
        ((er + 1) / (2 * er)) * Math.pow(Math.PI, 2));
    const x1 = 4 * (height / wEff) * ((14 * er + 8) / (11 * er));
    const temp3 = 4 * (height / wEff);
    const temp4 = INTRINSIC_FREESPACE_IMPEDANCE / (2 * Math.PI * Math.sqrt(2) * Math.sqrt(er + 1));
    const zo = temp4 * Math.log(1 + temp3 * (x1 + x2));
    return { zo, erEff };
}
function calculateErEff(width, height, er) {
    return width / height < 1
        ? (er + 1) / 2 +
            ((er - 1) / 2) * (Math.sqrt(width / (width + 12 * height)) + 0.04 * Math.pow(1 - width / height, 2))
        : (er + 1) / 2 + ((er - 1) / 2) * Math.sqrt(width / (width + 12 * height));
}
// See https://www.eeweb.com/tools/embedded-microstrip-impedance/ for the equations used here
export function embeddedMicrostrip(impedanceConfig) {
    const { height, er, heightFromPlane } = impedanceConfig;
    const zoSurf = microstrip(impedanceConfig);
    const b = height - heightFromPlane;
    const zoEmbed = zoSurf.zo *
        (1 /
            Math.sqrt(Math.exp((-2 * b) / heightFromPlane) + (er / zoSurf.erEff) * (1 - Math.exp((-2 * b) / heightFromPlane))));
    return { zo: zoEmbed, erEff: zoSurf.erEff };
}
// See https://www.eeweb.com/tools/edge-coupled-microstrip-impedance/ for the equations used here
export function edgeCoupledMicrostrip(impedanceConfig) {
    const { width: w, spacing: s, thickness: t, height: h, er } = impedanceConfig;
    const erEff = calculateErEff(w, h, er);
    const no = 377;
    const u = w / h;
    const a0 = 0.7287 * (erEff - 0.5 * (er + 1)) * (1 - Math.exp(-0.179 * u));
    const b0 = (0.747 * er) / (0.15 + er);
    const c0 = b0 - (b0 - 0.207) * Math.exp(-0.414 * u);
    const d0 = 0.593 + 0.694 * Math.exp(-0.562 * u);
    const g = s / h;
    const wEff = w +
        (t / Math.PI) *
            Math.log((4 * Math.exp(1)) / Math.sqrt(Math.pow(t / h, 2) + Math.pow(t / (w * Math.PI + 1.1 * t * Math.PI), 2))) *
            ((erEff + 1) / (2 * erEff));
    const erEffO = (0.5 * (er + 1) + a0 - erEff) * Math.exp(-c0 * Math.pow(g, d0)) + erEff;
    const zoSurf = (no / (2 * Math.PI * Math.sqrt(2) * Math.sqrt(erEff + 1))) *
        Math.log(1 +
            4 *
                (h / wEff) *
                (4 * (h / wEff) * ((14 * erEff + 8) / (11 * erEff)) +
                    Math.sqrt(16 * Math.pow(h / wEff, 2) * Math.pow((14 * erEff + 8) / (11 * erEff), 2) +
                        ((erEff + 1) / (2 * erEff)) * Math.pow(Math.PI, 2))));
    const q1 = 0.8695 * Math.pow(u, 0.194);
    const q2 = 1 + 0.7519 * g + 0.189 * Math.pow(g, 2.31);
    const q3 = 0.1975 +
        Math.pow(16.6 + Math.pow(8.4 / g, 6), -0.387) +
        (1 / 241) * Math.log(Math.pow(g, 10) / (1 + Math.pow(g / 3.4, 10)));
    const q4 = (2 * q1) / (q2 * (Math.exp(-g) * Math.pow(u, q3) + (2 - Math.exp(-g)) * Math.pow(u, -q3)));
    const q5 = 1.794 + 1.14 * Math.log(1 + 0.638 / (g + 0.517 * Math.pow(g, 2.43)));
    const q6 = 0.2305 +
        (1 / 281.3) * Math.log(Math.pow(g, 10) / (1 + Math.pow(g / 5.8, 10))) +
        (1 / 5.1) * Math.log(1 + 0.598 * Math.pow(g, 1.154));
    const q7 = (10 + 190 * Math.pow(g, 2)) / (1 + 82.3 * Math.pow(g, 3));
    const q8 = Math.exp(-6.5 - 0.95 * Math.log(g) - Math.pow(g / 0.15, 5));
    const q9 = Math.log(q7) * (q8 + 1 / 16.5);
    const q10 = (1 / q2) * (q2 * q4 - q5 * Math.exp(Math.log(u) * q6 * Math.pow(u, -q9)));
    // zoOdd - The impedance measured when only testing one of the differential traces referenced to the ground plane
    const zoOdd = (zoSurf * Math.sqrt(erEff / erEffO)) / (1 - (zoSurf / no) * q10 * Math.sqrt(erEff));
    const v = (u * (20 + Math.pow(g, 2))) / (10 + Math.pow(g, 2)) + g * Math.exp(-g);
    const aEv = 1 +
        Math.log((Math.pow(v, 4) + Math.pow(v / 52, 2)) / (Math.pow(v, 4) + 0.432)) / 49 +
        Math.log(1 + Math.pow(v / 18.1, 3)) / 18.7;
    const beEr = 0.564 * Math.pow((er - 0.9) / (er + 3), 0.053);
    const erEffE = 0.5 * (er + 1) + 0.5 * (er - 1) * (1 + Math.pow(10 / v, -aEv * beEr));
    // zoEven - The impedance measured when testing only one of the differential traces referenced to the ground plane
    const zoEven = (zoSurf * Math.sqrt(erEff / erEffE)) / (1 - (zoSurf / no) * q4 * Math.sqrt(erEff));
    // zoCommon - The impedance measured between the two lines when they are driven with the same signal.
    const zoCommon = zoEven / 2;
    // zoDiff - The impedance measured between the two lines when they are driven with opposite polarity signals
    const zoDiff = zoOdd * 2;
    return { zo: zoCommon, erEff, zoDiff, zoEven, zoOdd };
}
// See https://www.eeweb.com/tools/symmetric-stripline-impedance/ for the equations used here
export function symetricStripline(impedanceConfig) {
    const { width: w, thickness: t, height: h, er } = impedanceConfig;
    const b = 2 * h + t;
    const D = (w / 2) * (1 + (t / (Math.PI * w)) * (1 + Math.log((4 * Math.PI * w) / t)) + 0.551 * Math.pow(t / w, 2));
    const zo_ss_t2 = (60 / Math.sqrt(er)) * Math.log((4 * b) / (Math.PI * D));
    const theta = ((2 * b) / (b - t)) * Math.log((2 * b - t) / (b - t)) -
        (t / (b - t)) * Math.log((2 * b * t - Math.pow(t, 2)) / Math.pow(b - t, 2));
    const zo_ss_w2 = 94.15 / (w / b / (1 - t / b) + theta / Math.PI);
    const zo = w / b < 0.35 || t / b <= 0.25 || t / w <= 0.11 ? zo_ss_t2 : zo_ss_w2;
    return { zo: zo };
}
// See https://www.eeweb.com/tools/edge-coupled-stripline-impedance/ for the equations used here
export function edgeCoupledStripline(impedanceConfig) {
    const { width: w, spacing: s, thickness: t, height: b, er } = impedanceConfig;
    const no = INTRINSIC_FREESPACE_IMPEDANCE;
    const h = (b - t) / 2;
    const ke = tanh((Math.PI * w) / (2 * b)) * tanh(((Math.PI / 2) * (w + s)) / b);
    const ko = tanh((Math.PI * w) / (2 * b)) * coth(((Math.PI / 2) * (w + s)) / b);
    const kePrime = Math.sqrt(1 - Math.pow(ke, 2));
    const koPrime = Math.sqrt(1 - Math.pow(ko, 2));
    const Z0E0 = ((30 * Math.PI) / Math.sqrt(er)) * (eliptical(kePrime) / eliptical(ke));
    const Z0O0 = ((30 * Math.PI) / Math.sqrt(er)) * (eliptical(koPrime) / eliptical(ko));
    const ZoSs = symetricStripline({ ...impedanceConfig, height: h, width: w, spacing: s, thickness: t, er });
    const cfTb = ((0.0885 * er) / Math.PI) *
        (((2 * b) / (b - t)) * Math.log((2 * b - t) / (b - t)) -
            (t / (b - t)) * Math.log(Math.pow(b, 2) / Math.pow(b - t, 2) - 1));
    const cf0 = ((0.0885 * er) / Math.PI) * 2 * Math.log(2);
    const kIdeal = sech((Math.PI * w) / (2 * b));
    const kIdealPrime = tanh((Math.PI * w) / (2 * b));
    const Z0Ideal = (no / (4 * Math.sqrt(er))) * (eliptical(kIdeal) / eliptical(kIdealPrime));
    const zoOdd1 = 1 / (1 / ZoSs.zo - (cfTb / cf0) * (1 / Z0O0 - 1 / Z0Ideal));
    const zoOdd2 = 1 / (2 / Z0O0 - 1 / Z0Ideal - (2 / (no * er)) * (cfTb - cf0 - er / s));
    const zoOdd = s / t >= 5 ? zoOdd1 : zoOdd2;
    const zoEven = 1 / (1 / ZoSs.zo - (cfTb / cf0) * (1 / Z0Ideal - 1 / Z0E0));
    const zoCommon = zoEven / 2;
    const zoDiff = zoOdd * 2;
    return { zoOdd, zoEven: zoEven, zoCommon, zoDiff };
}
//# sourceMappingURL=impedanceEquations.js.map